import { useState } from 'react';
import { Button, Form, Input, Typography } from 'antd';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  CreditCardOutlined,
  DiffOutlined,
  GlobalOutlined,
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  SafetyOutlined,
  SyncOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { BarahBrand, BarahWordmark } from '@barahpay/shared';
import { useAuth } from '../auth/AuthContext';
import { useLanguage } from '../i18n/LanguageContext';
import { ApiErrorAlert } from '../components/Feedback';

interface FormValues {
  email: string;
  password: string;
  mfaCode?: string;
}

/** The capabilities the platform actually ships, as the brand panel's feature pills. */
const FEATURES: { key: string; icon: React.ReactNode }[] = [
  { key: 'featurePayments', icon: <CreditCardOutlined /> },
  { key: 'featureSubscriptions', icon: <SyncOutlined /> },
  { key: 'featureReconciliation', icon: <DiffOutlined /> },
  { key: 'featureAudit', icon: <SafetyOutlined /> },
];

const STATS = ['Scope', 'Languages', 'Monitoring'] as const;

export function LoginPage() {
  const { t } = useTranslation();
  const { signIn, mfaRequired } = useAuth();
  const { language, rtl, toggleLanguage } = useLanguage();
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(values: FormValues) {
    setSubmitting(true);
    setError(null);

    try {
      await signIn(values.email, values.password, values.mfaCode || undefined);
    } catch (caught) {
      setError(caught);
    } finally {
      setSubmitting(false);
    }
  }

  // The arrow points the way the reader is going, so it flips with the direction.
  const SubmitArrow = rtl ? ArrowLeftOutlined : ArrowRightOutlined;

  return (
    <div className="bp-login">
      {/* The brand panel: navy, dot-grid, gold glow — the Barah signature for a full-bleed surface. */}
      <aside className="bp-login__panel">
        <BarahBrand tone="onDark" size={40} />

        <div>
          <h1 className="bp-login__headline">{t('auth.headline')}</h1>
          <p className="bp-login__lede">{t('auth.lede')}</p>

          <div className="bp-login__pills">
            {FEATURES.map((feature) => (
              <span key={feature.key} className="bp-login__pill">
                {feature.icon}
                {t(`auth.${feature.key}`)}
              </span>
            ))}
          </div>
        </div>

        <div>
          <div className="bp-login__stats">
            {STATS.map((stat) => (
              <div key={stat}>
                <span className="bp-login__stat-value">{t(`auth.stat${stat}Value`)}</span>
                <span className="bp-login__stat-label">{t(`auth.stat${stat}Label`)}</span>
              </div>
            ))}
          </div>

          {/* Barah Pay is a Barah product, and the corporate lockup is what says so. */}
          <BarahWordmark tone="onDark" height={18} style={{ marginBlockStart: 28, opacity: 0.75 }} />
        </div>
      </aside>

      <main className="bp-login__form">
        <div className="bp-login__language">
          <Button type="text" size="small" icon={<GlobalOutlined />} onClick={toggleLanguage}>
            {language === 'ar' ? 'EN' : 'ع'}
          </Button>
        </div>

        <div className="bp-login__body">
          {/*
            Below the split-screen breakpoint the navy panel is hidden, so the mark moves here. The
            wrapper carries the visibility rule because the component sets `display` inline, which a
            class cannot override.
          */}
          <div className="bp-login__compact-brand">
            <BarahBrand size={38} />
          </div>

          <Typography.Title level={2} style={{ margin: 0, fontSize: 26 }}>
            {t('auth.welcome')}
          </Typography.Title>
          <Typography.Paragraph type="secondary" style={{ marginBlock: '8px 28px' }}>
            {t('auth.signInSubtitle')}
          </Typography.Paragraph>

          <ApiErrorAlert error={error} />

          <Form<FormValues>
            layout="vertical"
            onFinish={onSubmit}
            requiredMark={false}
            autoComplete="on"
            size="large"
          >
            <Form.Item
              name="email"
              label={t('auth.email')}
              rules={[{ required: true, message: t('common.required') }, { type: 'email' }]}
            >
              <Input
                prefix={<MailOutlined style={{ color: 'var(--bp-text-muted)' }} />}
                style={{ borderRadius: 'var(--bp-radius-lg)' }}
                autoComplete="username"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="password"
              label={t('auth.password')}
              rules={[{ required: true, message: t('common.required') }]}
            >
              <Input.Password
                prefix={<LockOutlined style={{ color: 'var(--bp-text-muted)' }} />}
                style={{ borderRadius: 'var(--bp-radius-lg)' }}
                autoComplete="current-password"
              />
            </Form.Item>

            {/* The code field appears only once the API has told us this account uses MFA. */}
            {mfaRequired ? (
              <Form.Item
                name="mfaCode"
                label={t('auth.mfaCode')}
                extra={t('auth.mfaRequired')}
                rules={[{ required: true, message: t('common.required') }]}
              >
                <Input
                  prefix={<SafetyCertificateOutlined style={{ color: 'var(--bp-text-muted)' }} />}
                  style={{ borderRadius: 'var(--bp-radius-lg)' }}
                  inputMode="numeric"
                  maxLength={8}
                  autoComplete="one-time-code"
                  autoFocus
                />
              </Form.Item>
            ) : null}

            <Button
              type="primary"
              htmlType="submit"
              block
              loading={submitting}
              icon={<SubmitArrow />}
              iconPosition="end"
              style={{ height: 46, marginBlockStart: 8, fontWeight: 600 }}
            >
              {t('auth.signIn')}
            </Button>
          </Form>
        </div>
      </main>
    </div>
  );
}
